import { useEffect, useState } from "react";
import { Phone, Menu, X, HardHat } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/brand";
import { cn } from "@/lib/utils";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("inicio");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = () => {
      const y = window.scrollY + 120;
      for (const link of NAV_LINKS) {
        const el = document.getElementById(link.id);
        if (el && el.offsetTop <= y && el.offsetTop + el.offsetHeight > y) {
          setActive(link.id);
          return;
        }
      }
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-[#0B1528]/95 backdrop-blur-md border-b border-white/10 shadow-lg"
          : "bg-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-3">
          {/* Logo */}
          <button
            data-testid="nav-logo"
            onClick={() => scrollTo("inicio")}
            className="flex items-center gap-2.5 group"
          >
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F26522] text-white shadow-lg shadow-orange-900/20 group-hover:rotate-6 transition-transform">
              <HardHat className="w-5 h-5" strokeWidth={2.5} />
            </span>
            <div className="text-left leading-tight">
              <div className="font-display font-extrabold text-white text-lg tracking-tight">
                AMLOU
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-mono-accent">
                Construcciones · Reformas
              </div>
            </div>
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-${link.id}`}
                onClick={() => scrollTo(link.id)}
                className={cn(
                  "px-3.5 py-2 rounded-full text-sm font-medium transition-all",
                  active === link.id
                    ? "text-white bg-white/10"
                    : "text-white/75 hover:text-white hover:bg-white/5"
                )}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-2">
            <a
              data-testid="nav-call-btn"
              href={BRAND.phonePrimaryHref}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/20 text-white/90 hover:bg-white/10 text-sm transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="font-mono-accent">{BRAND.phonePrimary}</span>
            </a>
            <button
              data-testid="nav-quote-cta"
              onClick={() => scrollTo("contacto")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F26522] hover:bg-[#D95212] text-white font-semibold text-sm shadow-lg shadow-orange-900/25 transition-all hover:-translate-y-0.5"
            >
              Solicitar Presupuesto
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-white hover:bg-white/10 transition-colors"
            aria-label="Menú"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div
            data-testid="nav-mobile-menu"
            className="lg:hidden pb-4 pt-2 border-t border-white/10 mt-1"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  data-testid={`nav-mobile-${link.id}`}
                  onClick={() => scrollTo(link.id)}
                  className="text-left px-4 py-3 rounded-lg text-white/85 hover:bg-white/5 hover:text-white text-sm font-medium"
                >
                  {link.label}
                </button>
              ))}
              <div className="flex flex-col gap-2 mt-3">
                <a
                  href={BRAND.phonePrimaryHref}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-white/20 text-white text-sm"
                >
                  <Phone className="w-4 h-4" /> {BRAND.phonePrimary}
                </a>
                <button
                  onClick={() => scrollTo("contacto")}
                  className="px-4 py-3 rounded-full bg-[#F26522] text-white font-semibold text-sm"
                >
                  Solicitar Presupuesto
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
